import React, {useMemo} from "react";
import {useNavigate} from "react-router-dom";
import {FormattedDate, FormattedMessage} from "react-intl";
import {defaultBorderColor, EmptyState, Icon, PageHeading, Spinner} from "peggirkit";
import {CalendarDaysIcon} from "@heroicons/react/20/solid";
import {PosOrderSummary} from "@/data/entities/order";
import {POS_ORDERS_QUERY_KEY, POS_ORDERS_REFRESH_INTERVAL, usePos} from "@/context/pos/PosProvider";
import {posOrders} from "@/routing/Router";
import {fromLocalDateString} from "@/utils/DateUtils";
import OrderListItem from "@widgets/pos/orders/sidebar/list/OrderListItem";
import {useRestaurant} from "@/context/restaurant/RestaurantProvider";
import {useQuery} from "@tanstack/react-query";
import {api} from "@/data/api";
import {twJoin} from "tailwind-merge";

type OrdersOfDay = {
    day: string, // YYYY-MM-DD
    orders: PosOrderSummary[],
};

/** Groups the (already sorted) orders by their fulfillment date. */
const groupByDay = (orders: PosOrderSummary[]): OrdersOfDay[] =>
    orders.reduce((groups: OrdersOfDay[], order: PosOrderSummary) => {
        const last: OrdersOfDay | undefined = groups[groups.length - 1];
        if (last !== undefined && last.day === order.fulfillmentDate)
            last.orders.push(order);
        else
            groups.push({day: order.fulfillmentDate, orders: [order]});
        return groups;
    }, []);

/**
 * All orders for a later day than today (placed in advance), per day.
 * Selecting one opens it in the order overview of its day.
 */
const UpcomingOrdersOverview = () => {
    const navigate = useNavigate();
    const {setSelectedDay} = usePos();
    const {dashboardLocation} = useRestaurant();
    const {data: orders, isPending} = useQuery({
        queryKey: [POS_ORDERS_QUERY_KEY, dashboardLocation.id, "upcoming"],
        queryFn: () => api.orders.listUpcomingOrders(dashboardLocation.id),
        refetchInterval: POS_ORDERS_REFRESH_INTERVAL,
    });
    const days: OrdersOfDay[] = useMemo(() => groupByDay(orders ?? []), [orders]);

    const open = (order: PosOrderSummary) => {
        setSelectedDay(order.fulfillmentDate);
        navigate(posOrders(order.cartPublicId).absolutePath());
    };

    return (
        <div className={"h-full overflow-y-auto"}>
            <section className={"w-full max-w-xl mx-auto px-4 py-6 flex flex-col gap-6"}>
                <PageHeading
                    title={<FormattedMessage id={"pos.upcoming.title"}/>}
                />

                {
                    isPending &&
                    <div className={"flex justify-center py-12"}>
                        <Spinner size={"lg"}/>
                    </div>
                }

                {
                    !isPending && days.length === 0 &&
                    <EmptyState
                        containerClassname={twJoin("border border-2 rounded-xl border-dashed p-6", defaultBorderColor)}
                        icon={<Icon icon={CalendarDaysIcon}/>}
                        title={<FormattedMessage id={"pos.upcoming.empty"}/>}
                        description={<FormattedMessage id={"pos.upcoming.emptyDescription"}/>}
                    />
                }

                {days.map(({day, orders}: OrdersOfDay) =>
                    <div key={day} className={"flex flex-col gap-2"}>
                        <h2 className={"text-sm font-semibold uppercase tracking-wide text-gray-600 dark:text-slate-400"}>
                            <FormattedDate
                                value={fromLocalDateString(day)}
                                weekday={"long"}
                                day={"numeric"}
                                month={"long"}
                            />
                            {" "}
                            <FormattedMessage id={"pos.upcoming.count"} values={{n: orders.length}}/>
                        </h2>
                        <ol className={"space-y-2"}>
                            {orders.map((order: PosOrderSummary) =>
                                <li key={order.cartPublicId}>
                                    <OrderListItem
                                        order={order}
                                        selected={false}
                                        onSelect={() => open(order)}
                                    />
                                </li>
                            )}
                        </ol>
                    </div>
                )}
            </section>
        </div>
    );
};

export default UpcomingOrdersOverview;